import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { media } from '../../../theme/libs/media';
import {
  FlexCol,
  FlexRow,
  FlexRowCenterVertical,
} from '@bart.sk-eshop/eshop-defaults';
import { theme } from '../../../theme/theme';

interface CountryPopupProps {
  detectedCountry: string;
  title: string;
  text: string;
  stayButton: string;
  switchButton: string;
  onStay: () => void;
  onSwitch: () => void;
  onClose: () => void;
}

const CountryPopup: React.FC<CountryPopupProps> = ({
  detectedCountry,
  title,
  text,
  stayButton,
  switchButton,
  onStay,
  onSwitch,
  onClose,
}) => {
  const showFlags = detectedCountry === 'SK' || detectedCountry === 'CZ';

  return (
    <Overlay onClick={onClose}>
      <PopupContainer onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">
          ×
        </CloseButton>

        <Title>{title}</Title>
        <Message>{text}</Message>

        {showFlags && (
          <FlagsContainer>
            <FlagButton
              onClick={detectedCountry === 'SK' ? onSwitch : onStay}
              title={
                detectedCountry === 'SK' ? 'Slovakia' : 'Stay on current site'
              }
            >
              <FlagImage
                src="https://flagcdn.com/w80/sk.png"
                alt="Slovakia"
                active={detectedCountry === 'SK'}
              />
              <FlagLabel>SK - Slovensko</FlagLabel>
            </FlagButton>

            <FlagButton
              onClick={detectedCountry === 'CZ' ? onSwitch : onStay}
              title={
                detectedCountry === 'CZ'
                  ? 'Czech Republic'
                  : 'Stay on current site'
              }
            >
              <FlagImage
                src="https://flagcdn.com/w80/cz.png"
                alt="Czech Republic"
                active={detectedCountry === 'CZ'}
              />
              <FlagLabel>CZ - Česká republika</FlagLabel>
            </FlagButton>
          </FlagsContainer>
        )}

        <ButtonsContainer>
          <StayButton onClick={onStay}>{stayButton}</StayButton>
          {switchButton && (
            <SwitchButton onClick={onSwitch}>{switchButton}</SwitchButton>
          )}
        </ButtonsContainer>
      </PopupContainer>
    </Overlay>
  );
};

export default CountryPopup;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${rem(20)};
`;

const PopupContainer = styled(FlexCol)`
  background: ${theme.color.white};
  border-radius: ${rem(12)};
  padding: ${rem(40)} ${rem(30)};
  max-width: ${rem(600)};
  width: 100%;
  position: relative;
  box-shadow: 0 ${rem(20)} ${rem(60)} rgba(0, 0, 0, 0.3);

  ${media(0, 'md')} {
    padding: ${rem(30)} ${rem(20)};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${rem(15)};
  right: ${rem(15)};
  background: none;
  border: none;
  font-size: ${rem(32)};
  color: ${theme.color.gray48};
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: ${theme.color.primary};
  }
`;

const Title = styled.h2`
  font-size: ${rem(28)};
  font-weight: 700;
  color: ${theme.color.primary};
  margin: 0 0 ${rem(20)} 0;
  text-align: center;

  ${media(0, 'md')} {
    font-size: ${rem(24)};
  }
`;

const Message = styled.p`
  font-size: ${rem(16)};
  line-height: 1.6;
  color: ${theme.color.textPrimary};
  margin: 0 0 ${rem(30)} 0;
  text-align: center;

  ${media(0, 'md')} {
    font-size: ${rem(14)};
  }
`;

const FlagsContainer = styled(FlexRow)`
  gap: ${rem(20)};
  justify-content: center;
  margin-bottom: ${rem(30)};

  ${media(0, 'md')} {
    gap: ${rem(15)};
  }
`;

const FlagButton = styled(FlexCol)`
  background: ${theme.color.white};
  border: ${rem(2)} solid ${theme.color.borderColor};
  border-radius: ${rem(8)};
  padding: ${rem(15)};
  cursor: pointer;
  transition: all 0.2s;
  align-items: center;
  gap: ${rem(10)};
  min-width: ${rem(120)};

  &:hover {
    border-color: ${theme.color.green};
    transform: translateY(${rem(-2)});
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.1);
  }

  ${media(0, 'md')} {
    padding: ${rem(12)};
    min-width: ${rem(100)};
  }
`;

const FlagImage = styled.img<{ active?: boolean }>`
  width: ${rem(60)};
  height: auto;
  border-radius: ${rem(4)};
  box-shadow: ${props =>
    props.active
      ? `0 0 0 ${rem(3)} ${theme.color.green}`
      : `0 ${rem(2)} ${rem(4)} rgba(0, 0, 0, 0.1)`};

  ${media(0, 'md')} {
    width: ${rem(50)};
  }
`;

const FlagLabel = styled.span`
  font-size: ${rem(13)};
  font-weight: 600;
  color: ${theme.color.primary};
  text-align: center;

  ${media(0, 'md')} {
    font-size: ${rem(11)};
  }
`;

const ButtonsContainer = styled(FlexRow)`
  gap: ${rem(15)};
  justify-content: center;
  flex-wrap: wrap;

  ${media(0, 'md')} {
    flex-direction: column;
  }
`;

const BaseButton = styled.button`
  padding: ${rem(14)} ${rem(28)};
  border-radius: ${rem(6)};
  font-size: ${rem(16)};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  min-width: ${rem(180)};

  ${media(0, 'md')} {
    font-size: ${rem(14)};
    padding: ${rem(12)} ${rem(20)};
    width: 100%;
  }
`;

const StayButton = styled(BaseButton)`
  background: ${theme.color.white};
  color: ${theme.color.primary};
  border: ${rem(2)} solid ${theme.color.borderColor};

  &:hover {
    background: ${theme.color.gray04};
    border-color: ${theme.color.primary};
  }
`;

const SwitchButton = styled(BaseButton)`
  background: ${theme.color.green};
  color: ${theme.color.white};

  &:hover {
    background: ${theme.color.stockCountAvailable};
    transform: translateY(${rem(-2)});
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(80, 158, 47, 0.3);
  }
`;
